import React from 'react'
import { cn } from '@/lib/utils'

/**
 * STANDARDIZED SLIDE LAYOUT SYSTEM
 *
 * This file defines the core layout system for all presentation slides.
 * All slides should use these layouts for consistency.
 *
 * Key Design Principles:
 * - Consistent padding: 12 (3rem = 48px) on all sides
 * - Safe content area to prevent cutoff
 * - Responsive typography scaling
 * - Standardized spacing between elements
 */

export interface BaseSlideLayoutProps {
  children: React.ReactNode
  className?: string
  background?: 'primary' | 'secondary' | 'accent' | 'light' | 'dark'
}

/**
 * Base Slide Layout - Foundation for all slides
 * Provides consistent padding and safe content area
 */
export const BaseSlideLayout: React.FC<BaseSlideLayoutProps> = ({
  children,
  className,
  background = 'light',
}) => {
  const bgClasses = {
    primary: 'bg-gradient-to-br from-primary-700 via-primary-600 to-primary-800 text-white',
    secondary: 'bg-gradient-to-br from-secondary-700 via-secondary-600 to-secondary-800 text-white',
    accent: 'bg-gradient-to-br from-accent-600 via-accent-500 to-accent-700 text-white',
    light: 'bg-white dark:bg-gray-950 text-gray-900 dark:text-white',
    dark: 'bg-gray-900 dark:bg-gray-950 text-white',
  }

  return (
    <div className={cn(
      'w-full h-full',
      'px-12 pt-12 pb-20', // 48px left/right/top, 80px bottom for navigation clearance
      'flex flex-col',
      'overflow-hidden', // Prevent content overflow
      bgClasses[background],
      className
    )}>
      {children}
    </div>
  )
}

/**
 * Slide Header - Standardized title and subtitle area
 */
export interface SlideHeaderProps {
  title: string
  subtitle?: string
  className?: string
  titleClassName?: string
  subtitleClassName?: string
}

export const SlideHeader: React.FC<SlideHeaderProps> = ({
  title,
  subtitle,
  className,
  titleClassName,
  subtitleClassName,
}) => {
  return (
    <header className={cn('mb-8 flex-shrink-0', className)}>
      <h1 className={cn(
        'text-4xl md:text-5xl font-bold mb-3 leading-tight',
        titleClassName
      )}>
        {title}
      </h1>
      {subtitle && (
        <h2 className={cn(
          'text-xl md:text-2xl opacity-90 leading-relaxed',
          subtitleClassName
        )}>
          {subtitle}
        </h2>
      )}
    </header>
  )
}

/**
 * Slide Content - Main content area with flexible layout
 */
export interface SlideContentProps {
  children: React.ReactNode
  className?: string
  centered?: boolean
}

export const SlideContent: React.FC<SlideContentProps> = ({
  children,
  className,
  centered = false,
}) => {
  return (
    <main className={cn(
      'flex-1 flex flex-col',
      'overflow-hidden', // CRITICAL: Never allow scrollbars in presentations
      centered && 'justify-center items-center',
      className
    )}>
      {children}
    </main>
  )
}

/**
 * Slide Footer - Bottom area for additional info, tips, or navigation hints
 */
export interface SlideFooterProps {
  children: React.ReactNode
  className?: string
}

export const SlideFooter: React.FC<SlideFooterProps> = ({
  children,
  className,
}) => {
  return (
    <footer className={cn(
      'mt-8 flex-shrink-0',
      className
    )}>
      {children}
    </footer>
  )
}

/**
 * Content Grid - Standardized grid layouts
 */
export interface ContentGridProps {
  children: React.ReactNode
  columns?: 1 | 2 | 3 | 4
  gap?: 'sm' | 'md' | 'lg'
  className?: string
}

export const ContentGrid: React.FC<ContentGridProps> = ({
  children,
  columns = 2,
  gap = 'md',
  className,
}) => {
  const gridClasses = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
  }

  const gapClasses = {
    sm: 'gap-4',
    md: 'gap-6',
    lg: 'gap-8',
  }

  return (
    <div className={cn(
      'grid',
      gridClasses[columns],
      gapClasses[gap],
      className
    )}>
      {children}
    </div>
  )
}
